import { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespRefineMenuDrawerAppliedFilterDisplay } from '../../finding/SbRespRefineMenuDrawer/SbRespRefineMenuDrawerAppliedFilterDisplay';
import { BUY_PAGE_FILTERS } from '../../constants/buyConstants';

// components
import { BuyMobileNavRefineMenuFacet } from './BuyMobileNavRefineMenuFacet';

function BuyMobileNavRefineMenuFiltersComponent({
    user,
    viewer,
    filters,
    currency,
    isClient,
    filterMap,
    itemSearch,
    initiallyExpanded,
}) {
    const { isSbTopNavVariant } = useContext(ServerVarsContext);
    const intl = useIntl();

    return (
        <Fragment>
            {filters.map(filter => {
                const { values, totalCount, name: filterName, localizedFilterName: title } = filter;
                if (!BUY_PAGE_FILTERS.includes(filterName)) {
                    return null;
                }
                const { component: FilterComponent, title: FilterTitle } =
                    filterMap[filterName] || {};
                const titleOverride = FilterTitle && intl.formatMessage(FilterTitle);

                return (
                    FilterComponent && (
                        <BuyMobileNavRefineMenuFacet
                            key={filterName}
                            storageFacetKey={filterName}
                            title={titleOverride || title}
                            initiallyExpanded={initiallyExpanded(filterName)}
                            appliedFiltersComponent={
                                isSbTopNavVariant ? (
                                    <SbRespRefineMenuDrawerAppliedFilterDisplay
                                        itemSearch={itemSearch}
                                        user={user}
                                        filterName={filterName}
                                        currency={currency}
                                    />
                                ) : null
                            }
                        >
                            <FilterComponent
                                user={user}
                                viewer={viewer}
                                values={values}
                                filter={filter}
                                filters={filters}
                                currency={currency}
                                isClient={isClient}
                                totalCount={totalCount}
                                filterName={filterName}
                                itemSearch={itemSearch}
                                shouldUseExpandableList={!isSbTopNavVariant}
                            />
                        </BuyMobileNavRefineMenuFacet>
                    )
                );
            })}
        </Fragment>
    );
}

BuyMobileNavRefineMenuFiltersComponent.defaultProps = {
    initiallyExpanded: () => true,
};

BuyMobileNavRefineMenuFiltersComponent.propTypes = {
    user: PropTypes.object,
    isClient: PropTypes.bool,
    filters: PropTypes.array.isRequired,
    viewer: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    filterMap: PropTypes.object.isRequired,
    itemSearch: PropTypes.object.isRequired,
    initiallyExpanded: PropTypes.func.isRequired,
    uriRef: PropTypes.string,
    isTrade: PropTypes.bool,
};

export const BuyMobileNavRefineMenuFilters = createFragmentContainer(
    BuyMobileNavRefineMenuFiltersComponent,
    {
        viewer: graphql`
            fragment BuyMobileNavRefineMenuFilters_viewer on Viewer {
                isMatchedSetsEnabled: isEnabled(key: "enableMatchedSetsFacet")
                ...SbMobileRefineMenuFilterDeliveryOptions_viewer
            }
        `,
        itemSearch: graphql`
            fragment BuyMobileNavRefineMenuFilters_itemSearch on ItemSearchQueryConnection {
                ...SbMobileRefineMenuFilterPrice_itemSearch
                ...SbMobileRefineMenuFilterDeliveryOptions_itemSearch
                appliedFilters {
                    name
                    values {
                        displayName
                    }
                }
            }
        `,
        filters: graphql`
            fragment BuyMobileNavRefineMenuFilters_filters on SearchBrowseFilter
            @relay(plural: true) {
                name
                totalCount
                localizedFilterName
                ...SbMobileRefineMenuFilterDimensions_filters
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    stats {
                        key
                        values
                    }
                }
            }
        `,
        user: graphql`
            fragment BuyMobileNavRefineMenuFilters_user on User {
                ...SbMobileRefineMenuFilterPrice_user
                ...SbMobileRefineMenuFilterDimensions_user
            }
        `,
    }
);
